// Gerenciar conexões (/allConnections) — mockup "Todas as Conexões": página
// FILHA de /connections, acessível pelo botão com selo SUPER no header de lá.
// Saiu do menu lateral em 09/2026.
//
// A listagem é por CONEXÃO (não agregada por empresa, como era antes): o
// nome da empresa e o plano vêm de /companies/list, casados por `companyId`
// — `/whatsapp/all` devolve a conexão sem a relação de empresa.
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SearchIcon from "@material-ui/icons/Search";
import ReplayIcon from "@material-ui/icons/Replay";
import SyncAltIcon from "@material-ui/icons/SyncAlt";

import MainContainer from "../../components/MainContainer";
import ForbiddenPage from "../../components/ForbiddenPage";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import useAllConnectionsStyles, { TABLE_GRID } from "./allConnectionsStyles";
import EditIcon from "@material-ui/icons/Edit";
import CropFreeIcon from "@material-ui/icons/CropFree";
import WhatsAppModalAdmin from "../../components/WhatsAppModalAdmin";
import QrcodeModal from "../../components/QrcodeModal";
import formatSerializedId from "../../utils/formatSerializedId";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { useTheme, alpha } from "@material-ui/core/styles";
import {
  superAdminTotals,
  filtraConexoes,
  statusMeta,
  statusLabel,
  aguardandoQr,
  numeroBruto,
  channelLabel,
} from "../Connections/connectionRules";

const iniciais = (nome = "") =>
  nome
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

const AllConnections = () => {
  const c = useCampaignPageStyles({ grid: TABLE_GRID });
  // Detalhes/edição usam o WhatsAppModalAdmin: ele lê e grava por
  // `whatsapp-admin/:id`, a rota que NÃO escopa pela empresa do usuário
  // logado — o modal comum de /connections não enxerga conexão de outra
  // empresa.
  const [editWhatsAppId, setEditWhatsAppId] = useState(null);
  const [qrWhatsApp, setQrWhatsApp] = useState(null);
  const classes = useAllConnectionsStyles();
  const theme = useTheme();
  const history = useHistory();

  const { user, socket } = useContext(AuthContext);
  const { list: listCompanies } = useCompanies();

  const [whats, setWhats] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [termo, setTermo] = useState("");
  const [status, setStatus] = useState("all");
  const [restarting, setRestarting] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [conexoes, empresas] = await Promise.all([
        api.get("/whatsapp/all/?session=0"),
        listCompanies(),
      ]);
      setWhats(conexoes.data || []);
      setCompanies(empresas || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // A sessão muda de estado pelo socket; sem isto a página ficaria mostrando
  // "Desconectada" até alguém recarregar.
  useEffect(() => {
    if (!socket) return undefined;
    const onWhatsapp = (data) => {
      if (data.action === "update" && data.whatsapp) {
        setWhats((prev) =>
          prev.map((w) => (w.id === data.whatsapp.id ? { ...w, ...data.whatsapp } : w))
        );
      }
    };
    const canal = `company-${user.companyId}-whatsapp`;
    socket.on(canal, onWhatsapp);
    return () => socket.off(canal, onWhatsapp);
  }, [socket, user]);

  const empresaPorId = useMemo(() => {
    const mapa = {};
    (companies || []).forEach((e) => {
      mapa[e.id] = e;
    });
    return mapa;
  }, [companies]);

  // Anexa a empresa a cada conexão antes de filtrar: a busca do mockup é
  // "Empresa ou número…", então o nome da empresa precisa estar no objeto.
  const comEmpresa = useMemo(
    () => (whats || []).map((w) => ({ ...w, company: empresaPorId[w.companyId] })),
    [whats, empresaPorId]
  );

  const visiveis = useMemo(
    () => filtraConexoes(comEmpresa, { termo, status }),
    [comEmpresa, termo, status]
  );

  const totais = useMemo(
    () => superAdminTotals(whats, companies),
    [whats, companies]
  );

  const handleRestartAll = async () => {
    setRestarting(true);
    try {
      await api.post("/whatsapp-restart/");
      toast.success(i18n.t("aeros.connections.all.restarted"));
    } catch (err) {
      toastError(err);
    } finally {
      setRestarting(false);
    }
  };

  const handleRestartOne = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
      toast.success(i18n.t("aeros.connections.all.restarted"));
    } catch (err) {
      toastError(err);
    }
  };

  if (!user?.super) return <ForbiddenPage />;

  const kpis = [
    { label: i18n.t("aeros.connections.all.kpiCompanies"), value: totais.companies },
    { label: i18n.t("aeros.connections.all.kpiConnections"), value: totais.connections },
    {
      label: i18n.t("aeros.connections.all.kpiConnected"),
      value: totais.connected,
      color: stateText(theme, AEROS.success, "success"),
    },
    {
      label: i18n.t("aeros.connections.all.kpiProblem"),
      value: totais.problem,
      color: stateText(theme, AEROS.error, "error"),
    },
  ];

  const renderRow = (w) => {
    const meta = statusMeta(w.status);
    const tom =
      meta.tone === "success" ? AEROS.success : meta.tone === "warning" ? AEROS.warning : AEROS.error;
    const empresa = w.company;
    return (
      <div key={w.id} className={c.tableRow}>
        <div className={classes.companyCell}>
          <span
            className={classes.companyAvatar}
            style={{ backgroundColor: alpha(AEROS.cyan, 0.8) }}
          >
            {iniciais(empresa?.name || "—")}
          </span>
          <div style={{ minWidth: 0 }}>
            <div className={classes.companyName} title={empresa?.name}>
              {empresa?.name || i18n.t("aeros.connections.all.noCompany")}
            </div>
            <div className={classes.companyPlan}>{empresa?.plan?.name || "—"}</div>
          </div>
        </div>

        <div className={c.tableCell}>
          <span className={classes.connName} title={w.name}>
            {w.name}
          </span>
        </div>

        <div className={`${c.tableCell} ${classes.mono}`}>
          {numeroBruto(w) ? formatSerializedId(numeroBruto(w)) : "—"}
        </div>

        <div className={c.tableCell}>
          <span
            className={c.statusPill}
            style={{
              color: stateText(theme, tom, meta.tone),
              backgroundColor: alpha(tom, 0.12),
            }}
          >
            <span className={c.statusDot} style={{ backgroundColor: tom }} />
            {statusLabel(w.status)}
          </span>
        </div>

        <div className={c.tableCell}>
          <span className={classes.channelTag}>{channelLabel(w.channel)}</span>
        </div>

        <div className={`${c.tableCell} ${c.cellMuted}`}>
          {w.updatedAt ? new Date(w.updatedAt).toLocaleDateString() : "—"}
        </div>

        <div className={c.cellActions}>
          {aguardandoQr(w) && (
            <Tooltip title={i18n.t("connections.buttons.qrcode")}>
              <IconButton
                className={c.rowActionButton}
                aria-label={i18n.t("connections.buttons.qrcode")}
                onClick={() => setQrWhatsApp(w)}
              >
                <CropFreeIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={i18n.t("aeros.connections.all.details")}>
            <IconButton
              className={c.rowActionButton}
              aria-label={i18n.t("aeros.connections.all.details")}
              onClick={() => setEditWhatsAppId(w.id)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={i18n.t("aeros.connections.all.restartOne")}>
            <IconButton
              className={c.rowActionButton}
              aria-label={i18n.t("aeros.connections.all.restartOne")}
              onClick={() => handleRestartOne(w.id)}
            >
              <ReplayIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  };

  return (
    <MainContainer>
      <WhatsAppModalAdmin
        open={Boolean(editWhatsAppId)}
        onClose={() => {
          setEditWhatsAppId(null);
          carregar();
        }}
        whatsAppId={editWhatsAppId}
      />
      {qrWhatsApp && (
        <QrcodeModal
          open={Boolean(qrWhatsApp)}
          onClose={() => setQrWhatsApp(null)}
          whatsAppId={qrWhatsApp.id}
        />
      )}
      <div className={c.pageHeader}>
        <div>
          <div className={classes.breadcrumb}>
            <Button
              className={classes.backButton}
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push("/connections")}
            >
              {i18n.t("aeros.connections.all.back")}
            </Button>
            <ChevronRightIcon />
            <span className={classes.currentCrumb}>
              {i18n.t("aeros.connections.all.title")}
            </span>
            <span className={classes.superBadge}>
              {i18n.t("aeros.connections.super")}
            </span>
          </div>
        </div>
        <div className={c.headerActions}>
          <InputBase
            className={c.searchPill}
            placeholder={i18n.t("aeros.connections.all.search")}
            type="search"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("aeros.connections.all.search") }}
          />
          <TextField
            select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            variant="outlined"
            size="small"
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="all">{i18n.t("aeros.connections.all.filterAll")}</MenuItem>
            <MenuItem value="connected">
              {i18n.t("aeros.connections.all.filterConnected")}
            </MenuItem>
            <MenuItem value="problem">
              {i18n.t("aeros.connections.all.filterProblem")}
            </MenuItem>
          </TextField>
          <Button
            variant="outlined"
            className={c.outlineButton}
            startIcon={<ReplayIcon />}
            onClick={handleRestartAll}
            disabled={restarting}
          >
            {i18n.t("aeros.connections.all.restartAll")}
          </Button>
        </div>
      </div>

      <div className={c.pageBody}>
        <div className={classes.kpiGrid}>
          {kpis.map((k) => (
            <div key={k.label} className={classes.kpiCard}>
              <div className={classes.kpiLabel}>{k.label}</div>
              <div
                className={classes.kpiValue}
                style={k.color ? { color: k.color } : undefined}
              >
                {k.value}
              </div>
            </div>
          ))}
        </div>

        <div className={c.tableCard}>
          <div className={c.tableHeadRow}>
            <div className={c.tableHeadCell}>
              {i18n.t("aeros.connections.all.company")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("aeros.connections.all.connection")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("aeros.connections.all.number")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("aeros.connections.all.status")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("aeros.connections.all.channel")}
            </div>
            <div className={c.tableHeadCell}>
              {i18n.t("aeros.connections.all.updated")}
            </div>
            <div className={`${c.tableHeadCell} ${c.cellActionsHead}`}>
              {i18n.t("aeros.connections.all.actions")}
            </div>
          </div>

          {loading && whats.length === 0 ? (
            <div className={c.loadingRow}>…</div>
          ) : visiveis.length === 0 ? (
            <div className={c.emptyState}>
              <span className={c.emptyIcon}>
                <SyncAltIcon />
              </span>
              <div className={c.emptyTitle}>
                {i18n.t("aeros.connections.all.empty.title")}
              </div>
              <div className={c.emptyText}>
                {i18n.t("aeros.connections.all.empty.text")}
              </div>
            </div>
          ) : (
            visiveis.map(renderRow)
          )}
        </div>
      </div>
    </MainContainer>
  );
};

export default AllConnections;
